import { GenaiRequest } from "@cohesive/api-client";

/**
 * The canonical shape of an action explanation — a fast-model, plain-language
 * account of what an action (e.g. a sandbox shell command) will do if approved.
 * Reusable anywhere an action needs explaining; only the prompt that produces it
 * is surface-specific.
 */
export interface ActionExplanation {
  /** One plain-language sentence — the headline answer. */
  summary: string;
  /** True when the action only inspects/reads and changes nothing. */
  read_only: boolean;
  /** Concrete step-by-step account of what the action does. */
  what_happens: string[];
  /** Specific external effects: things sent, created, or modified. Empty when read-only / none. */
  data_touched: string[];
  reversibility: "reversible" | "reversible-with-effort" | "irreversible";
  /** Short note on the reversibility classification. */
  reversibility_note: string;
}

/**
 * JSON Schema for {@link ActionExplanation}, passed to the genai endpoint as
 * `output_schema`. Every property is required and `additionalProperties` is
 * false so it satisfies strict structured-output validation.
 */
export const ACTION_EXPLANATION_SCHEMA: NonNullable<
  GenaiRequest["output_schema"]
> = {
  type: "object",
  additionalProperties: false,
  properties: {
    summary: {
      type: "string",
      description:
        "One plain-language sentence a non-technical user can understand, describing the net result if approved.",
    },
    read_only: {
      type: "boolean",
      description:
        "True only if the action inspects or reads and changes nothing outside the sandbox.",
    },
    what_happens: {
      type: "array",
      items: { type: "string" },
      description:
        "Ordered, concrete steps describing what the action does. One short sentence each.",
    },
    data_touched: {
      type: "array",
      items: { type: "string" },
      description:
        "Specific external effects grounded in the input: messages sent, files created or modified, records changed (include recipients, filenames, destinations when present). Empty array if read-only or none can be inferred.",
    },
    reversibility: {
      type: "string",
      enum: ["reversible", "reversible-with-effort", "irreversible"],
      description: "How hard it is to undo the action's effects.",
    },
    reversibility_note: {
      type: "string",
      description:
        "One short sentence explaining the reversibility classification.",
    },
  },
  required: [
    "summary",
    "read_only",
    "what_happens",
    "data_touched",
    "reversibility",
    "reversibility_note",
  ],
};

const REVERSIBILITY_VALUES: ReadonlySet<ActionExplanation["reversibility"]> =
  new Set(["reversible", "reversible-with-effort", "irreversible"]);

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string" && v.trim() !== "")
    : [];

/**
 * Coerce the endpoint's `structured_output` (typed `unknown`) into an
 * {@link ActionExplanation}, tolerating missing/odd fields. Returns null when
 * there isn't even a usable summary, so callers can show an error state.
 */
export function parseActionExplanation(
  value: unknown,
): ActionExplanation | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  const summary = typeof v.summary === "string" ? v.summary.trim() : "";
  if (!summary) return null;

  const reversibility = REVERSIBILITY_VALUES.has(
    v.reversibility as ActionExplanation["reversibility"],
  )
    ? (v.reversibility as ActionExplanation["reversibility"])
    : "reversible-with-effort";

  return {
    summary,
    read_only: v.read_only === true,
    what_happens: asStringArray(v.what_happens),
    data_touched: asStringArray(v.data_touched),
    reversibility,
    reversibility_note:
      typeof v.reversibility_note === "string"
        ? v.reversibility_note.trim()
        : "",
  };
}
